import { IPIconButton, IPLink } from 'baseComponents';
import { IPDropdown } from 'baseComponents/Dropdown/IPDropdown';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { deleteIcon, globalStackTokensGapMedium, globalStackTokensGapSmall } from 'globalStyles';
import { useTranslation } from 'react-i18next';
import { CopilotMarkdown } from './Conversation/CopilotMarkdown';

export interface CopilotDocumentVersionOption {
  key: string;
  version: number;
  created?: Date;
  savedLibraryLinkId?: number;
}

interface ICopilotDocumentProps {
  markdown: string | undefined;
  isStreaming: boolean;
  streamContent?: string;
  isOnPanel?: boolean;
  versions?: CopilotDocumentVersionOption[];
  selectedVersionKey?: string;
  onVersionChange?: (key: string) => void;
  onDeleteVersion?: (key: string) => void;
}

export const CopilotDocument = (props: ICopilotDocumentProps) => {
  const { t } = useTranslation(['copilot']);

  // Agent-created markdown documents render read-only from their markdown. While the agent is streaming a
  // new document we show the streamed markdown; once complete we show the stored block markdown.
  const documentMarkdown = props.isStreaming ? props.streamContent ?? '' : props.markdown ?? '';

  //
  // Main render
  //
  const createLibraryLinkUrl = (linkId: number) => `/library/links?id=${linkId}`;

  const versionOptions = (props.versions ?? []).map((version) => {
    const versionText = t('copilot:Chat.Document.VersionItem', {
      version: version.version,
      date: version.created ? version.created.toLocaleString() : '',
    });

    return {
      key: version.key,
      text: version.savedLibraryLinkId ? `${versionText} ${t('copilot:Chat.Document.Saved')}` : versionText,
      element: (
        <IPStack
          horizontal
          verticalAlign="center"
          tokens={globalStackTokensGapSmall}
          styles={{ root: { width: '100%' } }}
        >
          <IPStack.Item grow>{versionText}</IPStack.Item>
          {version.savedLibraryLinkId && (
            <IPStack.Item>
              <IPLink
                href={createLibraryLinkUrl(version.savedLibraryLinkId)}
                target="_blank"
                rel="noopener noreferrer"
                styles={{ root: { color: '#107c10', fontWeight: 600 } }}
                onClick={(event) => event.stopPropagation()}
              >
                {t('copilot:Chat.Document.Saved')}
              </IPLink>
            </IPStack.Item>
          )}
          <IPStack.Item>
            <IPIconButton
              iconProps={deleteIcon}
              title={t('copilot:Chat.Document.DeleteVersion')}
              disabled={props.isStreaming}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                props.onDeleteVersion?.(version.key);
              }}
            />
          </IPStack.Item>
        </IPStack>
      ),
    };
  });
  const versionDropdown = versionOptions.length > 0 && (
    <IPStack.Item>
      <IPDropdown
        label={t('copilot:Chat.Document.VersionLabel')}
        selectedKey={props.selectedVersionKey ?? versionOptions[0]?.key}
        options={versionOptions}
        disabled={props.isStreaming}
        onChange={(_event, option) => {
          if (option?.key !== undefined) props.onVersionChange?.(String(option.key));
        }}
      />
    </IPStack.Item>
  );

  return (
    <IPStack
      verticalFill
      tokens={globalStackTokensGapMedium}
      styles={{ root: { height: '100%', minHeight: 0, overflowX: 'hidden', overflowY: 'auto', paddingRight: 10 } }}
    >
      {versionDropdown}
      <IPStack.Item grow styles={{ root: { flexBasis: 0, minHeight: 0 } }}>
        <CopilotMarkdown content={documentMarkdown} withTableStyles={true} skipHtml />
      </IPStack.Item>
    </IPStack>
  );
};
